import Vue from 'vue'
import config from '@/assets/js/conf/config'
import { createMerchantNotificationWS, ConnectionState } from '@/assets/js/utils/merchantNotificationWS'

export const NotificationType = {
    IMPORT_PROGRESS: 'import_progress',
    SYSTEM: 'system',
    TASK: 'task'
}

export const NotificationTypeLabel = {
    [NotificationType.IMPORT_PROGRESS]: '导入进度',
    [NotificationType.SYSTEM]: '系统消息',
    [NotificationType.TASK]: '任务通知'
}

function buildStableNotificationId(type, data) {
    return [
        type || data.type || 'message',
        data.created_at || data.create_time || '',
        data.title || '',
        data.content || data.message || ''
    ].join('|')
}

function normalizeNotification(type, payload) {
    const data = { ...(payload || {}) }

    if (!data.id && data.notification_id) {
        data.id = data.notification_id
    }
    if (!data.id) {
        // Centrifugo history 会反复恢复同一条消息，没有服务端 id 时使用内容生成稳定 id。
        data.id = buildStableNotificationId(type, data)
    }
    if (!data.type) {
        data.type = type || 'message'
    }
    // 导入进度通知兼容 data.percent/status 结构，方便现有 UI 展示。
    if (data.type === NotificationType.IMPORT_PROGRESS && data.data && typeof data.data === 'object') {
        if (data.data.percent !== undefined) {
            data.progress = parseInt(data.data.percent)
        }
        if (data.data.status) {
            data.status = data.data.status
        }
    }

    return data
}

function getReadIds() {
    try {
        const userInfoStr = localStorage.getItem('userinfo')
        const userId = userInfoStr ? JSON.parse(userInfoStr).id || '' : ''
        const key = `read_notification_ids_${userId}`
        const ids = localStorage.getItem(key)
        return ids ? new Set(JSON.parse(ids)) : new Set()
    } catch (e) {
        return new Set()
    }
}

function saveReadIds(readIds) {
    try {
        const userInfoStr = localStorage.getItem('userinfo')
        const userId = userInfoStr ? JSON.parse(userInfoStr).id || '' : ''
        const key = `read_notification_ids_${userId}`
        const array = Array.from(readIds).slice(-200) // Keep last 200 items to avoid bloating localStorage
        localStorage.setItem(key, JSON.stringify(array))
    } catch (e) {
        // ignore
    }
}

export default {
    namespaced: true,

    state: {
        notifications: [],             // 仅保存当前浏览器会话内收到的实时通知
        lastAddedNotification: null,
        wsClient: null,
        wsState: ConnectionState.DISCONNECTED,
        wsReconnectAttempts: 0,
        wsLastError: null,
        loading: false,
        pagination: {
            page: 1,
            pageSize: 20,
            total: 0
        }
    },

    getters: {
        allNotifications: state => state.notifications,
        wsState: state => state.wsState,
        isWSConnected: state => state.wsState === ConnectionState.CONNECTED,
        isWSReconnecting: state => state.wsState === ConnectionState.RECONNECTING,
        isWSFailed: state => state.wsState === ConnectionState.FAILED,
        wsReconnectAttempts: state => state.wsReconnectAttempts,
        getNotificationsByType: state => type => state.notifications.filter(n => n.type === type),
        unreadCount: state => state.notifications.filter(n => !n.isRead).length
    },

    mutations: {
        UPSERT_NOTIFICATION(state, payload) {
            const notification = payload && payload.notification ? payload.notification : payload
            const silent = !!(payload && payload.silent)
            const readIds = getReadIds()
            const index = state.notifications.findIndex(n => n.id === notification.id)
            
            // 如果本地已读记录里有，则是已读。否则默认为未读（false）。
            const isRead = readIds.has(notification.id)

            if (index === -1) {
                Vue.set(notification, 'isRead', isRead)
                state.notifications.unshift(notification)
            } else {
                const existing = state.notifications[index]
                Vue.set(state.notifications, index, {
                    ...existing,
                    ...notification,
                    isRead: existing.isRead !== undefined ? existing.isRead : isRead
                })
            }

            if (!silent) {
                Vue.set(state, 'lastAddedNotification', {
                    ...notification,
                    _timestamp: Date.now()
                })
            }
        },

        MARK_ALL_AS_READ(state) {
            const readIds = getReadIds()
            state.notifications.forEach(n => {
                Vue.set(n, 'isRead', true)
                if (n.id) {
                    readIds.add(n.id)
                }
            })
            saveReadIds(readIds)
        },

        MARK_ONE_AS_READ(state, payload) {
            const notification = payload
            if (!notification) return
            Vue.set(notification, 'isRead', true)
            if (notification.id) {
                const readIds = getReadIds()
                readIds.add(notification.id)
                saveReadIds(readIds)
            }
        },

        DELETE_NOTIFICATION(state, payload) {
            const notification = payload
            if (!notification) return
            const index = state.notifications.findIndex(n => n.id === notification.id)
            if (index > -1) {
                state.notifications.splice(index, 1)
            }
        },

        SET_WS_CLIENT(state, client) {
            state.wsClient = client
        },

        SET_WS_STATE(state, { status, attempts, error }) {
            if (status !== undefined) {
                state.wsState = status
            }
            if (attempts !== undefined) {
                state.wsReconnectAttempts = attempts
            }
            if (error !== undefined) {
                state.wsLastError = error
            }
        },

        SET_LOADING(state, loading) {
            state.loading = loading
        },

        SET_PAGINATION(state, { page, pageSize, total }) {
            if (page !== undefined) state.pagination.page = page
            if (pageSize !== undefined) state.pagination.pageSize = pageSize
            if (total !== undefined) state.pagination.total = total
        }
    },

    actions: {
        initNotificationWS({ commit, dispatch, state }) {
            const token = localStorage.getItem(config.tokenKey)
            if (!token) {
                return
            }

            if (state.wsClient) {
                state.wsClient.close()
                commit('SET_WS_CLIENT', null)
            }

            const client = createMerchantNotificationWS({
                handlers: {
                    onConnected() {
                        commit('SET_WS_STATE', {
                            status: ConnectionState.CONNECTED,
                            attempts: 0,
                            error: null
                        })
                    },
                    onNotification({ type, payload, source }) {
                        const notification = normalizeNotification(type, payload)
                        const isHistory = source === 'history'
                        commit('UPSERT_NOTIFICATION', {
                            notification,
                            silent: isHistory
                        })
                        if (!isHistory) {
                            dispatch('showDesktopNotification', notification)
                        }
                    },
                    onError(message, code) {
                        commit('SET_WS_STATE', { error: message })

                        if (code === 'AUTH_FAILED' || code === 109) {
                            setTimeout(() => {
                                dispatch('disconnectNotificationWS')
                                commit('login/OUT_LOGIN', null, { root: true })
                                if (window.$router) {
                                    window.$router.push('/login')
                                } else {
                                    window.location.href = '/login'
                                }
                            }, 100)
                        }
                    },
                    onClose() {
                        commit('SET_WS_STATE', { status: ConnectionState.DISCONNECTED })
                    },
                    onStateChange(status, attempts) {
                        commit('SET_WS_STATE', { status, attempts })
                    }
                }
            })

            commit('SET_WS_CLIENT', client)
        },

        disconnectNotificationWS({ commit, state }) {
            if (state.wsClient) {
                state.wsClient.close()
                commit('SET_WS_CLIENT', null)
            }
            commit('SET_WS_STATE', { status: ConnectionState.DISCONNECTED, attempts: 0 })
        },

        reconnectNotificationWS({ state }) {
            if (state.wsClient) {
                state.wsClient.reconnect()
            }
        },

        markAllAsRead({ commit }) {
            commit('MARK_ALL_AS_READ')
        },

        fetchNotifications() {
            // 实时通知不再持久化，历史列表只展示当前会话内消息。
            return Promise.resolve({ code: 200, data: [] })
        },

        fetchUnreadCount() {
            return Promise.resolve({ code: 200, data: { count: 0 } })
        },

        showDesktopNotification(_, notification) {
            if (!('Notification' in window)) {
                return
            }

            if (Notification.permission === 'granted') {
                new Notification(notification.title || '新消息', {
                    body: notification.content || notification.message,
                    icon: '/favicon.ico'
                })
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission()
            }
        }
    }
}
